/**
 * Кнопки действий страницы спецификации: Estimation Request, Order, Export Excel, Export PDF.
 *
 * Валидация статусов выбранных товаров перед отправкой на бэк:
 *  - calculation (to_count) требует статус 1 (not calculated)
 *  - order (to_pay) требует статус 3 (calculated) + заполненные адреса
 *
 * После успешной отправки остаёмся на спецификации и перерисовываем только таблицу
 * через loadSpecTable() (AJAX), без полной перезагрузки страницы — иначе тост об успехе
 * исчезает вместе с reload:
 *  - calculation: товар переходит в статус "On calculation" (виден клиенту) — перерисовка покажет его
 *  - order: заказ не виден клиенту до одобрения менеджером (ARK-2071)
 */

import { apiRequest, APIError } from '../../api.js';
import { state } from './store.js';
import { loadSpecTable } from './table.js';
import { exportSelectedToExcel, exportSelectedToPdf } from './export.js';

function parseSpecStatus(row) {
    const statusValue = String(row?.dataset?.statusId || 'spec_1');
    if (!statusValue.startsWith('spec_')) return null;
    return parseInt(statusValue.replace('spec_', ''));
}

function closestSpecRow(el) {
    return el.closest('tr.row-item, .adm_spec_item_grid_mobile_wrap');
}

function validateAddressesBeforeOrder() {
    const shipAddress = document.getElementById('ship_address')?.value || '';
    const billAddress = document.getElementById('bill_address')?.value || '';

    const errors = [];
    if (!shipAddress.trim()) errors.push('Ship to address is not filled');
    if (!billAddress.trim()) errors.push('Bill to address is not filled');

    if (errors.length > 0) {
        window.showToast('error', errors.join('. ') + '. Please fill in the addresses on the INFO page.');
        return false;
    }
    return true;
}

export function updateActionButtons() {
    const selectedCheckboxes = document.querySelectorAll('.checkbox-select-product:checked');
    const hasSelection = selectedCheckboxes.length > 0;

    let hasNotCalculated = false;
    let hasCalculated = false;

    selectedCheckboxes.forEach(checkbox => {
        const row = closestSpecRow(checkbox);
        const statusId = parseSpecStatus(row);
        if (statusId === 1) hasNotCalculated = true;
        else if (statusId === 3) hasCalculated = true;
    });

    const estimationBtn = document.getElementById('estimation-request-btn');
    if (estimationBtn) estimationBtn.disabled = !hasNotCalculated;

    const orderBtn = document.getElementById('order-btn');
    if (orderBtn) orderBtn.disabled = !hasCalculated;

    const excel = document.getElementById('export-excel-btn');
    if (excel) excel.disabled = !hasSelection;

    const pdf = document.getElementById('export-pdf-btn');
    if (pdf) pdf.disabled = !hasSelection;
}

async function executeAction(payload, actionType) {
    const actionText = actionType === 'to_count' ? 'calculation' : 'order';
    document.querySelectorAll('#estimation-request-btn, #order-btn').forEach(btn => { btn.disabled = true; });

    try {
        const response = await apiRequest(`/sendprojecttocount/${state.projectId}`, {
            method: 'POST',
            body: payload,
        });

        if (response && response.success) {
            window.showToast('success', response.message || `Products successfully sent for ${actionText}`);

            // И to_pay, и to_count остаются на спецификации: перерисовываем таблицу AJAX-ом,
            // чтобы статусы обновились с сервера, а тост об успехе не пропал из-за reload.
            // Полный reload/redirect — только запасной путь для нештатных actionType.
            if (actionType === 'to_pay' || actionType === 'to_count') {
                loadSpecTable();
            } else if (response.redirect) {
                window.location.href = response.redirect;
            } else {
                window.location.reload();
            }
        } else {
            window.showToast('error', TOAST_MESSAGES.ORDER.PROCESSING_ERROR);
            updateActionButtons();
        }
    } catch (e) {
        console.error('Error sending products:', e);
        const msg = e instanceof APIError ? e.message : `Ошибка при отправке на ${actionText}`;
        window.showToast('error', msg);
        updateActionButtons();
    }
}

async function sendSelectedProducts(actionType) {
    if (actionType === 'to_pay' && !validateAddressesBeforeOrder()) return;

    const selectedCheckboxes = document.querySelectorAll('.checkbox-select-product:checked');
    if (selectedCheckboxes.length === 0) {
        window.showToast('warning', TOAST_MESSAGES.PRODUCT.SELECT_REQUIRED);
        return;
    }

    const uniqueProductIds = new Set();
    const productStatusMap = new Map();

    selectedCheckboxes.forEach(checkbox => {
        const productId = checkbox.value;
        const row = closestSpecRow(checkbox);
        const statusId = parseSpecStatus(row) || 0;

        uniqueProductIds.add(productId);
        productStatusMap.set(productId, statusId);
    });

    const payload = { type: actionType, project_id: state.projectId };

    let validCount = 0;
    let invalidCount = 0;
    uniqueProductIds.forEach(productId => {
        const statusId = productStatusMap.get(productId);
        if ((actionType === 'to_count' && statusId === 1) ||
            (actionType === 'to_pay' && statusId === 3)) {
            payload[`product_${productId}`] = '1';
            payload[`status_${productId}`] = statusId;
            validCount++;
        } else {
            invalidCount++;
        }
    });

    const totalSelected = uniqueProductIds.size;

    if (validCount === 0) {
        const msg = actionType === 'to_count'
            ? 'Selected products are already sent for calculation or have different status'
            : 'Selected products are not calculated or have different status';
        window.showToast('warning', msg);
        return;
    }

    if (invalidCount > 0) {
        const statusText = actionType === 'to_count' ? 'not calculated' : 'calculated';
        const confirmed = await window.showConfirmation(
            `Out of ${totalSelected} selected products, only ${validCount} have the required status (${statusText}). Continue with these products?`
        );
        if (confirmed) await executeAction(payload, actionType);
        return;
    }

    await executeAction(payload, actionType);
}

export function initActionButtons() {
    updateActionButtons();

    // Чекбокс товара — делегирование
    document.addEventListener('change', e => {
        if (e.target.matches('.checkbox-select-product')) {
            updateActionButtons();
        } else if (e.target.matches('.checkbox-select-all')) {
            // Выбрать все по комнате. Скоуп — .adm_spec_item (select-all чекбокс
            // лежит в <thead>, мобильный — вне таблицы, поэтому closest('tbody') не годится).
            const isChecked = e.target.checked;
            const specItem = e.target.closest('.adm_spec_item');
            if (specItem) {
                specItem.querySelectorAll('.checkbox-select-product').forEach(cb => {
                    if (cb.checked !== isChecked) {
                        cb.checked = isChecked;
                        // Дёргаем change, чтобы table.js обновил state.selectedProducts.
                        cb.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            }
            updateActionButtons();
        }
    });

    // Estimation Request
    document.addEventListener('click', async e => {
        const btn = e.target.closest('#estimation-request-btn');
        if (!btn) return;
        e.preventDefault();

        if (btn.disabled) {
            window.showToast('warning', TOAST_MESSAGES.PRODUCT.SELECT_FOR_CALCULATION);
            return;
        }

        const budgetValue = document.getElementById('project-budget')?.value || '';
        const hasBudget = budgetValue.trim().length > 0 && parseFloat(budgetValue) > 0;

        // Бюджет задан — отправляем сразу. Попап нужен только чтобы предупредить,
        // что бюджет проекта не установлен.
        if (hasBudget) {
            sendSelectedProducts('to_count');
            return;
        }

        const message = 'Send selected products for calculation? Note: Project budget is not set.';
        if (typeof window.showConfirmation === 'function') {
            const confirmed = await window.showConfirmation(message, 'Confirm Calculation Request');
            if (confirmed) sendSelectedProducts('to_count');
        } else if (confirm(message)) {
            sendSelectedProducts('to_count');
        }
    });

    // Order
    document.addEventListener('click', e => {
        const btn = e.target.closest('#order-btn');
        if (!btn) return;
        e.preventDefault();

        if (btn.disabled) {
            window.showToast('warning', TOAST_MESSAGES.PRODUCT.SELECT_CALCULATED);
            return;
        }
        sendSelectedProducts('to_pay');
    });

    // Export Excel
    document.addEventListener('click', e => {
        const btn = e.target.closest('#export-excel-btn');
        if (!btn) return;
        e.preventDefault();

        if (btn.disabled) {
            window.showToast('warning', TOAST_MESSAGES.PRODUCT.SELECT_FOR_EXPORT);
            return;
        }
        exportSelectedToExcel();
    });

    // Export PDF
    document.addEventListener('click', e => {
        const btn = e.target.closest('#export-pdf-btn');
        if (!btn) return;
        e.preventDefault();

        if (btn.disabled) {
            window.showToast('warning', TOAST_MESSAGES.PRODUCT.SELECT_FOR_EXPORT);
            return;
        }
        exportSelectedToPdf();
    });
}
